/**
 * "Gerade los" — what is happening in the child's life right now (2026-08-29,
 * the balanced version of the UX review's item 4: one optional dropdown chip,
 * no extra row, no mandatory decision). The chosen event goes to the ideas
 * endpoint as `context` (every card weaves it in) and pre-selects the matching
 * lesson. Nothing is stored on the user.
 */
import type { CreateStoryData } from './types';

export const LIFE_EVENT_KEYS = [
  'first_school_day', 'dentist', 'new_sibling', 'dark', 'moving', 'birthday', 'christmas', 'holiday',
] as const;
export type LifeEventKey = (typeof LIFE_EVENT_KEYS)[number];

export const LIFE_EVENT_EMOJI: Record<LifeEventKey, string> = {
  first_school_day: '🏫', dentist: '🦷', new_sibling: '👶', dark: '🌙',
  moving: '🏠', birthday: '🎂', christmas: '🎄', holiday: '✈️',
};

/** Lesson (config.MORAL_OPTIONS key) that fits the event, if any. */
export const LIFE_EVENT_LESSON: Partial<Record<LifeEventKey, NonNullable<CreateStoryData['moral']>>> = {
  first_school_day: 'first_day_school',
  dentist: 'fears',
  new_sibling: 'new_sibling',
  dark: 'fears',
};

/**
 * Seasonal events: [fromMonth, fromDay, toMonth, toDay]; listed first inside the window, hidden outside it.
 * first_school_day runs to 31 Oct (was 30 Sep, 2026-09-06): the settling-in after the Einschulung
 * lasts until the Herbstferien — parents' own finish line in the 75-comment thread
 * (docs/halo/fb-search-method-2026-09.md); Bavaria/BW only start mid-September.
 */
const SEASONAL: Partial<Record<LifeEventKey, [number, number, number, number]>> = {
  first_school_day: [7, 15, 10, 31],
  christmas: [11, 15, 12, 26],
};

export function isLifeEventKey(value: string | null | undefined): value is LifeEventKey {
  return !!value && (LIFE_EVENT_KEYS as readonly string[]).includes(value);
}

function inWindow(w: [number, number, number, number], now: Date): boolean {
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  return md >= w[0] * 100 + w[1] && md <= w[2] * 100 + w[3];
}

/** Events to offer today: in-window seasonal ones first, evergreen ones next, out-of-window seasonal ones hidden. */
export function visibleLifeEvents(now: Date = new Date()): LifeEventKey[] {
  const seasonalNow = LIFE_EVENT_KEYS.filter((k) => SEASONAL[k] && inWindow(SEASONAL[k]!, now));
  const evergreen = LIFE_EVENT_KEYS.filter((k) => !SEASONAL[k]);
  return [...seasonalNow, ...evergreen];
}

export type LifeSelection = { kind: 'key'; key: LifeEventKey } | { kind: 'custom'; text: string } | null;
